import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Trash2, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * History- und Scheduled-Overview-Panel.
 * Zeigt:
 *   - Aktive/geplante Broadcasts (mit Cancel-Button)
 *   - Vergangene Broadcasts (Immediate + Scheduled) mit Delivery-Rate
 *
 * Endpoint: GET /api/admin/firebase/broadcast-history
 */
const BroadcastHistory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/firebase/broadcast-history?limit=100`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const cancelBroadcast = async (id) => {
    if (!window.confirm('Diesen geplanten Broadcast wirklich abbrechen?')) return;
    try {
      await axios.delete(`${API}/admin/firebase/scheduled/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('Broadcast abgebrochen');
      load();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  const statusBadge = (status) => {
    const map = {
      scheduled: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Geplant' },
      sending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Wird gesendet' },
      sent: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Gesendet' },
      cancelled: { icon: XCircle, color: 'bg-gray-100 text-gray-600', label: 'Abgebrochen' },
      failed: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Fehlgeschlagen' },
    };
    const m = map[status] || map.sent;
    const Icon = m.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded ${m.color}`}>
        <Icon className="w-3 h-3" />
        {m.label}
      </span>
    );
  };

  const formatDate = (s) => {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  const upcoming = items.filter((i) => i.status === 'scheduled' || i.status === 'sending');
  const past = items.filter((i) => !['scheduled', 'sending'].includes(i.status));

  return (
    <div className="space-y-4" data-testid="broadcast-history">
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-500" />
          Broadcast-Historie
        </h3>
        <p className="text-sm text-gray-500">
          Alle gesendeten und geplanten Push-Broadcasts.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Lädt…</p>
      ) : (
        <>
          {/* Upcoming Scheduled */}
          {upcoming.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold">
                Geplant ({upcoming.length})
              </p>
              <div className="space-y-2">
                {upcoming.map((i) => (
                  <div
                    key={i.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-3"
                    data-testid={`scheduled-${i.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 truncate">{i.title}</p>
                        {statusBadge(i.status)}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{i.body}</p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        📅 {formatDate(i.scheduled_at)}
                      </p>
                    </div>
                    {i.status === 'scheduled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelBroadcast(i.id)}
                        className="text-red-500 border-red-500/40 hover:bg-red-500/10"
                        data-testid={`cancel-scheduled-${i.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Past Broadcasts */}
          {past.length > 0 ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold mt-4">
                Vergangene Broadcasts ({past.length})
              </p>
              <div className="space-y-2">
                {past.slice(0, 20).map((i, idx) => (
                  <div
                    key={i.id || idx}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                    data-testid={`past-broadcast-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 truncate">{i.title}</p>
                          {statusBadge(i.status)}
                          <span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {i.source === 'scheduled' ? 'geplant' : 'sofort'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{i.body}</p>
                        <p className="text-[11px] text-gray-500 mt-1">
                          📤 {formatDate(i.sent_at)}
                        </p>
                      </div>
                      {(i.sent_count > 0 || i.failed_count > 0) && (
                        <div className="text-right text-xs flex-shrink-0">
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            <span className="text-gray-900 font-bold">{i.sent_count}</span>
                          </div>
                          {i.failed_count > 0 && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <AlertCircle className="w-3 h-3 text-red-500" />
                              <span className="text-red-400">{i.failed_count}</span>
                            </div>
                          )}
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            {i.delivery_rate}% zugestellt
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            upcoming.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">
                Noch keine Broadcasts gesendet.
              </p>
            )
          )}
        </>
      )}
    </div>
  );
};

export default BroadcastHistory;
